// Desktop Menu with Full-Width Slide-in Functionality
class DesktopMenu {
  constructor() {
    this.menuContainer = document.querySelector('[data-desktop-menu-container]');
    this.menuTriggers = document.querySelectorAll('[data-desktop-menu-trigger]');
    this.menuContents = document.querySelectorAll('.desktop-menu-content');
    this.header = document.getElementById('site-header');
    this.logo = document.getElementById('logo');
    this.overlay = document.getElementById('desktop-menu-overlay');
    
    this.isOpen = false;
    this.currentMenuIndex = null;
    this.contentTimeout = null;
    this.isTransitioning = false;
    
    this.init();
  }

  init() {
    if (!this.menuContainer || !this.menuTriggers.length) return;
    
    this.bindEvents();
  }

  bindEvents() {
    // Add click events to menu triggers
    this.menuTriggers.forEach((trigger, index) => {
      trigger.addEventListener('click', (e) => this.handleMenuClick(e, index));
    });

    // Add click event to close menu when clicking outside
    document.addEventListener('click', (e) => this.handleDocumentClick(e));
    
    // ESC key to close menu
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.hideMenu();
      }
    });

    // Add event delegation for level 3 toggles
    this.menuContainer.addEventListener('click', (e) => this.handleLevel3Toggle(e));
  }

  handleMenuClick(event, menuIndex) {
    event.preventDefault();
    event.stopPropagation();

    // If menu is already open with same content, close it
    if (this.isOpen && this.currentMenuIndex === menuIndex && !this.isTransitioning) {
      this.hideMenu();
      return;
    }

    // If menu is already open but user is clicking a different item, switch content
    if (this.isOpen && this.currentMenuIndex !== menuIndex) {
      this.showMenu(menuIndex);
    } else {
      // Open menu with selected content
      this.showMenu(menuIndex);
    }
  }

  handleDocumentClick(event) {
    // Don't close if clicking on menu triggers or menu container
    if (!this.isOpen) return;
    
    const clickedTrigger = event.target.closest('[data-desktop-menu-trigger]');
    const clickedContainer = event.target.closest('[data-desktop-menu-container]');
    
    if (!clickedTrigger && !clickedContainer) {
      this.hideMenu();
    }
  }

  handleLevel3Toggle(event) {
    const toggleButton = event.target.closest('[data-level-3-toggle]');
    if (!toggleButton) return;

    event.preventDefault();
    event.stopPropagation();

    const expandableContainer = toggleButton.closest('.level-3-expandable');
    const submenu = expandableContainer.querySelector('.level-3-submenu');
    const isExpanded = expandableContainer.getAttribute('data-expanded') === 'true';

    if (isExpanded) {
      // Collapse
      expandableContainer.setAttribute('data-expanded', 'false');
      toggleButton.setAttribute('aria-expanded', 'false');
      submenu.style.maxHeight = '0';
    } else {
      // Expand
      expandableContainer.setAttribute('data-expanded', 'true');
      toggleButton.setAttribute('aria-expanded', 'true');
      
      // Calculate the actual height needed
      const scrollHeight = submenu.scrollHeight;
      submenu.style.maxHeight = scrollHeight + 'px';
    }
  }

  clearTimeouts() {
    if (this.contentTimeout) {
      clearTimeout(this.contentTimeout);
      this.contentTimeout = null;
    }
  }

  showMenu(menuIndex) {
    // Clear any existing timeouts
    this.clearTimeouts();
    
    const targetContent = document.querySelector(`[data-menu-content="${menuIndex}"]`);
    if (!targetContent) return;

    // Update aria-expanded for all triggers
    this.menuTriggers.forEach((trigger, index) => {
      trigger.setAttribute('aria-expanded', index === menuIndex ? 'true' : 'false');
    });
    
    // If menu is already open but switching content
    if (this.isOpen && this.currentMenuIndex !== menuIndex) {
      // Hide all content immediately
      this.menuContents.forEach((content, index) => {
        if (index !== menuIndex) {
          content.classList.remove('is-visible');
        }
      });
      
      // Show new content immediately
      targetContent.classList.add('is-visible');
      this.currentMenuIndex = menuIndex;
      
      return;
    }

    // Initial menu opening
    if (!this.isOpen) {
      this.isTransitioning = true;
      
      // Hide all content initially
      this.menuContents.forEach(content => {
        content.classList.remove('is-visible');
      });

      // Show target content
      targetContent.classList.add('is-visible');
      
      // Trigger slide-in animation
      this.menuContainer.classList.add('is-open');
      
      // Add menu-open class to header for styling
      this.header.classList.add('menu-open');
      
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      
      // Show overlay
      if (this.overlay) {
        this.overlay.classList.remove('opacity-0', 'pointer-events-none');
        this.overlay.classList.add('opacity-50', 'pointer-events-auto');
      }
      
      // Content is already visible, just mark as not transitioning
      this.contentTimeout = setTimeout(() => {
        this.isTransitioning = false;
      }, 75);
      
      this.isOpen = true;
      this.currentMenuIndex = menuIndex;
    }
  }

  hideMenu() {
    if (!this.isOpen) return;

    // Clear any pending timeouts
    this.clearTimeouts();
    this.isTransitioning = true;

    // Reset all level 3 expanded states
    this.resetLevel3States();

    // Update aria-expanded for all triggers
    this.menuTriggers.forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    // Hide overlay
    if (this.overlay) {
      this.overlay.classList.remove('opacity-50', 'pointer-events-auto');
      this.overlay.classList.add('opacity-0', 'pointer-events-none');
    }

    // Trigger slide-out animation AND header color change simultaneously
    this.menuContainer.classList.remove('is-open');
    this.header.classList.remove('menu-open');
    
    // Fade out content while sliding (simultaneous animations)
    this.menuContents.forEach(content => {
      content.classList.remove('is-visible');
    });
    
    // Unlock body scroll
    document.body.style.overflow = '';
    
    // Clean up after slide animation completes
    this.contentTimeout = setTimeout(() => {
      this.isTransitioning = false;
    }, 150); // Wait for slide animation to complete

    this.isOpen = false;
    this.currentMenuIndex = null;
  }


  resetLevel3States() {
    // Reset all level 3 expanded states
    const expandableContainers = this.menuContainer.querySelectorAll('.level-3-expandable');
    expandableContainers.forEach(container => {
      container.setAttribute('data-expanded', 'false');
      const button = container.querySelector('[data-level-3-toggle]');
      const submenu = container.querySelector('.level-3-submenu');
      
      if (button) button.setAttribute('aria-expanded', 'false');
      if (submenu) submenu.style.maxHeight = '0';
    });
  }

}

// Initialize desktop menu when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.desktopMenu = new DesktopMenu();
});
